/// <amd-module name="Artifacts/FeedSettings/hooks/DetailsTabForm" />
import { IFeedSettingsHubState } from "Artifacts/FeedSettings/redux";
import * as Res from "Artifacts/FeedSettings/Resources";
import { IFeedSettings } from "Artifacts/FeedSettings/types";
import { IDetailsTabFormState } from "Artifacts/FeedSettings/types/IDetailsTabFormState";
import { getStore } from "Artifacts/Root/Redux";
import { detailsSettingsRoute } from "Artifacts/Root/routing/Constants";
import { IFeed } from "Artifacts/Root/types";
import { isValidFeedName } from "Artifacts/Root/Validator";
import { useEffect, useReducer, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";
import * as StringUtils from "VSS/Core/Util/String";
import { useComponentContext } from "VSS/Platform/Layout";

export interface IDetailsTabFormHook {
  formState: IDetailsTabFormState;
  isFormDirty: boolean;
  resetForm: () => void;
  onFeedNameChange: (value: string) => void;
  onFeedDescriptionChange: (value: string) => void;
  onHideDeletedPackageVersionsChange: (value: boolean) => void;
  onBadgesEnabledChange: (value: boolean) => void;
  onRetentionPoliciesEnabledChange: (value: boolean) => void;
  onRetentionPolicyDaysLimitChange: (value: string) => void;
  onRetentionPolicyCountLimitChange: (value: string) => void;
  onSaveSuccessful: () => void;
  errors: IDetailTabErrors;
}

export interface DetailsTabFormItemError {
  description: string;
}

export interface IDetailTabErrors {
  feedNameError?: DetailsTabFormItemError;
  retentionMaxNumVersionsError?: DetailsTabFormItemError;
  retentionDaysToKeepError?: DetailsTabFormItemError;
}

function validationError(
  error: keyof IDetailTabErrors,
  description: string,
): (errors: IDetailTabErrors) => IDetailTabErrors {
  return (errors: IDetailTabErrors): IDetailTabErrors => ({
    ...errors,
    [error]: { description },
  });
}

function validationOk(
  error: keyof IDetailTabErrors,
): (errors: IDetailTabErrors) => IDetailTabErrors {
  return (errors) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [error]: _, ...rest } = errors;
    return rest;
  };
}

export function useDetailsTabForm(feed?: IFeed, settings?: IFeedSettings): IDetailsTabFormHook {
  const [isFormDirty, setFormDirty] = useState<boolean>(false);
  const [isNameFieldDirty, setNameFieldDirty] = useState<boolean>(false);
  const [formState, dispatch] = useReducer(
    feedDetailsFormReducer,
    getDefaultFormState(feed, settings),
  );

  useEffect(() => {
    if (feed && settings) {
      dispatch({ type: "form", value: getDefaultFormState(feed, settings) });
    }
  }, [feed, settings]);

  const dispatchAndMakeDirty = (action: FeedDetailsFormAction) => {
    if (action.type === "feedName") {
      setNameFieldDirty(true);
    }
    setFormDirty(true);
    dispatch(action);
  };

  const resetForm = () => {
    setFormDirty(false);
    dispatch({ type: "form", value: getDefaultFormState(feed, settings) });
  };

  const history = useHistory();
  const onSaveSuccessful = () => {
    if (!settings) {
      return;
    }
    settings.feedName = formState.feedName;
    settings.description = formState.feedDescription;
    settings.hideDeletedPackageVersions = formState.hideDeletedPackageVersions;
    settings.isBadgesEnabled = formState.badgesEnabled;
    settings.retention.isEnabled = formState.retentionPoliciesEnabled;
    settings.retention.daysToKeepRecentPackages = +formState.retentionPolicyDaysLimit;
    settings.retention.packageVersionCountLimit = +formState.retentionPolicyCountLimit;
    if (isNameFieldDirty) {
      history.replace(generatePath(detailsSettingsRoute, { feedIdentifier: formState.feedName }));
    }
    resetForm();
  };

  const onFeedNameChange = (value: string) => dispatchAndMakeDirty({ type: "feedName", value });
  const onFeedDescriptionChange = (value: string) =>
    dispatchAndMakeDirty({ type: "feedDescription", value });
  const onHideDeletedPackageVersionsChange = (value: boolean) =>
    dispatchAndMakeDirty({ type: "hideDeletedPackageVersions", value });
  const onBadgesEnabledChange = (value: boolean) =>
    dispatchAndMakeDirty({ type: "badgesEnabled", value });
  const onRetentionPoliciesEnabledChange = (value: boolean) =>
    dispatchAndMakeDirty({ type: "retentionPoliciesEnabled", value });
  const onRetentionPolicyDaysLimitChange = (value: string) =>
    dispatchAndMakeDirty({ type: "retentionPolicyDaysLimit", value });
  const onRetentionPolicyCountLimitChange = (value: string) =>
    dispatchAndMakeDirty({ type: "retentionPolicyCountLimit", value });

  const [errors] = useDetailsTabFormValidation(formState);

  return {
    formState,
    isFormDirty,
    resetForm,
    onFeedNameChange,
    onFeedDescriptionChange,
    onHideDeletedPackageVersionsChange,
    onBadgesEnabledChange,
    onRetentionPoliciesEnabledChange,
    onRetentionPolicyCountLimitChange,
    onRetentionPolicyDaysLimitChange,
    onSaveSuccessful,
    errors,
  };
}

interface FeedDetailsFormAction {
  type:
    | "feedName"
    | "feedDescription"
    | "hideDeletedPackageVersions"
    | "badgesEnabled"
    | "retentionPoliciesEnabled"
    | "retentionPolicyDaysLimit"
    | "retentionPolicyDaysLimit"
    | "retentionPolicyCountLimit"
    | "form";
  value: boolean | string | IDetailsTabFormState;
}

function feedDetailsFormReducer(
  state: IDetailsTabFormState,
  action: FeedDetailsFormAction,
): IDetailsTabFormState {
  switch (action.type) {
    case "feedName":
      return { ...state, feedName: action.value as string };
    case "feedDescription":
      return { ...state, feedDescription: action.value as string };
    case "badgesEnabled":
      return { ...state, badgesEnabled: action.value as boolean };
    case "retentionPoliciesEnabled":
      return { ...state, retentionPoliciesEnabled: action.value as boolean };
    case "retentionPolicyCountLimit":
      return { ...state, retentionPolicyCountLimit: action.value as string };
    case "retentionPolicyDaysLimit":
      return { ...state, retentionPolicyDaysLimit: action.value as string };
    case "form":
      return { ...state, ...(action.value as IDetailsTabFormState) };
    case "hideDeletedPackageVersions":
      return { ...state, hideDeletedPackageVersions: action.value as boolean };
  }
}

function getDefaultFormState(feed?: IFeed, settings?: IFeedSettings): IDetailsTabFormState {
  return {
    feedName: feed?.name || "",
    feedDescription: settings?.description || "",
    hideDeletedPackageVersions: settings?.hideDeletedPackageVersions || false,
    badgesEnabled: settings?.isBadgesEnabled || false,
    retentionPoliciesEnabled: settings?.retention?.isEnabled || false,
    retentionPolicyCountLimit:
      settings?.retention?.packageVersionCountLimit?.toString() ||
      settings?.retention?.defaultCountLimit?.toString() ||
      "",
    retentionPolicyDaysLimit:
      settings?.retention?.daysToKeepRecentPackages?.toString() ||
      settings?.retention?.defaultDaysToKeepLimit?.toString() ||
      "",
  };
}

function useDetailsTabFormValidation(formState: IDetailsTabFormState): [IDetailTabErrors] {
  const [errors, setErrors] = useState<IDetailTabErrors>({});

  useFeedNameValidator(formState, setErrors);
  useRetentionMaxNumVersionsValidator(formState, setErrors);
  useRetentionDaysToKeepValidator(formState, setErrors);

  return [errors];
}

function useFeedNameValidator(
  formState: IDetailsTabFormState,
  setErrors: React.Dispatch<React.SetStateAction<IDetailTabErrors>>,
): void {
  useEffect(() => {
    if (!isValidFeedName(formState.feedName)) {
      return setErrors(validationError("feedNameError", Res.DetailsTab_FeedNameError));
    }

    return setErrors(validationOk("feedNameError"));
  }, [formState.feedName, setErrors]);
}

function useRetentionMaxNumVersionsValidator(
  formState: IDetailsTabFormState,
  setErrors: React.Dispatch<React.SetStateAction<IDetailTabErrors>>,
): void {
  const { pageContext } = useComponentContext();

  useEffect(() => {
    const feedSettingsState = getStore(pageContext).getState()["feed-settings"]
      .hubState as IFeedSettingsHubState;

    const minimumCountLimit = feedSettingsState.settings?.retention?.minimumCountLimit;
    const maximumCountLimit = feedSettingsState.settings?.retention?.maximumCountLimit;

    retentionValidator(
      formState,
      setErrors,
      formState.retentionPolicyCountLimit,
      minimumCountLimit,
      maximumCountLimit,
      "retentionMaxNumVersionsError",
      Res.DetailsTab_Error_InvalidPackageRetentionVersionCountLimit,
    );
  }, [pageContext, formState, setErrors]);
}

function useRetentionDaysToKeepValidator(
  formState: IDetailsTabFormState,
  setErrors: React.Dispatch<React.SetStateAction<IDetailTabErrors>>,
): void {
  const { pageContext } = useComponentContext();

  useEffect(() => {
    const feedSettingsState = getStore(pageContext).getState()["feed-settings"]
      .hubState as IFeedSettingsHubState;

    const minimumDaysToKeepLimit = feedSettingsState.settings?.retention?.minimumDaysToKeepLimit;
    const maximumDaysToKeepLimit = feedSettingsState.settings?.retention?.maximumDaysToKeepLimit;

    retentionValidator(
      formState,
      setErrors,
      formState.retentionPolicyDaysLimit,
      minimumDaysToKeepLimit,
      maximumDaysToKeepLimit,
      "retentionDaysToKeepError",
      Res.DetailsTab_Error_InvalidPackageRetentionDaysToKeepLimit,
    );
  }, [pageContext, formState, setErrors]);
}

function retentionValidator(
  formState: IDetailsTabFormState,
  setErrors: React.Dispatch<React.SetStateAction<IDetailTabErrors>>,
  limitToCheck: string,
  minValue: number | undefined,
  maxValue: number | undefined,
  errorMessageKey: keyof IDetailTabErrors,
  errorMessage: string,
) {
  // Re-constructing this function so that we can accurately check for integers.
  Number.isInteger =
    Number.isInteger ||
    function(value) {
      return typeof value === "number" && isFinite(value) && Math.floor(value) === value;
    };

  // if the check mark is not selected, then we don't care about the values.
  // See the FeedSettingService for further details.
  if (!formState.retentionPoliciesEnabled) {
    return setErrors(validationOk(errorMessageKey));
  }

  // Can't have an empty input.
  if (limitToCheck.trim() === "") {
    return setErrors(
      validationError(
        errorMessageKey,
        StringUtils.format(errorMessage, limitToCheck, minValue, maxValue),
      ),
    );
  }

  // this means that the value is not an integer and probably has letters.
  if (limitToCheck != null && !Number.isInteger(Number(limitToCheck))) {
    return setErrors(
      validationError(
        errorMessageKey,
        StringUtils.format(errorMessage, limitToCheck, minValue, maxValue) +
          Res.DetailsTab_IntegersOnly,
      ),
    );
  }

  // Value is not within the limits
  if (
    minValue &&
    maxValue &&
    !ValidateRetentionPolicyInput(Number(limitToCheck), minValue, maxValue)
  ) {
    return setErrors(
      validationError(
        errorMessageKey,
        StringUtils.format(errorMessage, limitToCheck, minValue, maxValue),
      ),
    );
  }

  return setErrors(validationOk(errorMessageKey));
}

function ValidateRetentionPolicyInput(
  versions: number,
  minValue: number,
  maxValue: number,
): boolean {
  return !isNaN(versions) && versions <= maxValue && versions >= minValue && versions % 1 === 0;
}
